import { types } from "mobx-state-tree";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Transport } from "modules/common/models/transport";
import { apiUrls } from "modules/common/services/communication/urls";
import { texts } from "modules/common/texts";

/** Веха проекта: одна дата с названием */
export interface Milestone {
    id: string;
    orderId: string;
    /** yyyy-MM-dd */
    day: string;
    name: string;
    comment: string;
}

/** Черновик для формы: у новой вехи id пустой */
export interface MilestoneDraft {
    id: string;
    orderId: string;
    day: string;
    name: string;
    comment: string;
}

export interface OrderMilestones {
    canEdit: boolean;
    items: Milestone[];
}

/**
 * Дата вехи в API — «YYYY-MM-DD», как у дней планового графика. Не SORTABLE_DATE_FORMAT:
 * тот с точками («YYYY.MM.DD»), и сервер такую дату вехи не разбирает.
 */
export const MILESTONE_DAY_FORMAT = "YYYY-MM-DD";

/** Длина названия вехи — как OrderMilestone.NAME_MAX_LENGTH на бэке */
export const MILESTONE_NAME_MAX_LENGTH = 120;

/**
 * Вехи проекта: общий транспорт для планового графика и блока «Вехи» в карточке проекта —
 * оба места работают с одними данными. Состояния не держит: кто загрузил, тот и хранит.
 */
export const MilestonesStore = types
    .compose(Transport, Notificator, types.model({}))
    .actions((self) => ({
        load: flow(function* (orderId: string) {
            try {
                const data: OrderMilestones = yield self.transport.get<OrderMilestones>(
                    apiUrls.projectPlan.milestones(orderId)
                );

                return data;
            } catch (er) {
                self.notify.error(er);
                return null;
            }
        }),

        save: flow(function* (draft: MilestoneDraft) {
            try {
                const saved: Milestone = yield self.transport.post<Milestone>(apiUrls.projectPlan.saveMilestone, draft);
                self.notify.success(texts.messages.saved);

                return saved;
            } catch (er) {
                self.notify.error(er);
                return null;
            }
        }),

        remove: flow(function* (milestone: Milestone) {
            try {
                yield self.transport.delete<boolean>(
                    apiUrls.projectPlan.removeMilestone(milestone.orderId, milestone.id)
                );

                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            }
        }),
    }))
    .named("MilestonesStore");

export type MilestonesStoreType = typeof MilestonesStore.Type;
